import { isCustomerParticipantRole } from "../../../lib/access";
import { ProjectAccessRole } from "../../../lib/account";
import { useProject } from "../../../shell/project";
import { grantRole } from "../../../workspaces/library/api";
import { useEffect } from "react";
import { Outlet, createFileRoute, useLocation, useSearch } from "@tanstack/react-router";
import { retainedQueryData } from "@sx/api-client";
import { Page, PageHeader, Skeleton } from "@sx/ui";
import { deliveryReviewOnly, useLibraryAccess } from "../../../workspaces/library/access";

import {
  registerWorkspaceHeader,
  type ProjectAddress,
  type WorkspaceNavItem,
} from "../../../lib/nav";
import { WorkspaceAccessDenied, WorkspaceGate } from "../../../shell/WorkspaceGate";
import { useProjectAddress, useProjectId } from "../../../shell/project";
import { LibraryProjectProvider } from "../../../workspaces/library/project";
import { prefetchForProject } from "../../../lib/prefetch";

export const Route = createFileRoute("/_console/p/$org/$project/library")({
  // The grant is asked beside the page's own reads, never before them.
  loader: ({ context: { queryClient }, params }) =>
    prefetchForProject(
      queryClient,
      params,
      Promise.all([
        import("../../../workspaces/library/api"),
        import("../../../workspaces/library/queries"),
      ]),
      ([{ createCatalogApi }, { libraryQueries }], projectId) =>
        void queryClient.prefetchQuery(
          libraryQueries(projectId, createCatalogApi(projectId)).access,
        ),
    ),
  component: LibraryLayout,
});

/* The Library's header items. `end` on Browse is the old NavLink semantics: the index item
 * matches its own address exactly, the section items match by prefix — otherwise Browse
 * would light up on every screen in the workspace. */
const libraryNav = (params: ProjectAddress): readonly WorkspaceNavItem[] => [
  {
    to: "/p/$org/$project/library",
    params,
    label: "Browse",
    icon: "frames",
    end: true,
  },
  {
    to: "/p/$org/$project/library/search",
    params,
    label: "Search",
    icon: "search",
  },
  {
    to: "/p/$org/$project/library/map",
    params,
    label: "Map",
    icon: "map",
  },
  {
    to: "/p/$org/$project/library/issues",
    params,
    label: "Issues",
    icon: "report-problem",
  },
];

/* Who you are was settled one layer up, once, against the auth door. What is settled here is
 * narrower and genuinely the catalog's to answer: whether this operator holds a grant in this
 * tenant's library. A 403 is therefore a destination — a scoped denial — not a sign-in. */
function LibraryLayout() {
  const projectId = useProjectId();
  const role = useProject().selected?.managedAccessRole;
  const reviewOnly = role === ProjectAccessRole.RESULT_REVIEWER;
  const search = useSearch({ strict: false });
  const address = useProjectAddress();
  const pathname = useLocation({ select: (location) => location.pathname });
  const libraryIndex = pathname.replace(/\/$/, "") === `/p/${address.org}/${address.project}/library`;
  const publishedView = libraryIndex &&
    (reviewOnly || search.result !== undefined || String(search.published) === "true");
  const me = useLibraryAccess(!reviewOnly && !publishedView);

  // Withdrawn on the way out, so a workspace that is no longer on screen cannot paint the
  // header of the one that replaced it; withheld while denied, because nav to screens the
  // door will refuse is an invitation to a dead end.
  //
  // The catalog's authority is a (scope, role) matrix, so the account badges name both:
  // "writer" alone would overstate a credential granted only `skl.eval.*`.
  const scopes = retainedQueryData(me)?.scopes;
  const decisionOnly = deliveryReviewOnly(scopes ?? []);
  useEffect(() => {
    return registerWorkspaceHeader({
      workspace: "experience-library",
      items: decisionOnly ? [{ to: "/p/$org/$project/library", params: address, label: "Delivery review", icon: "query", end: true }] : reviewOnly || (publishedView && scopes === undefined)
        ? [
            {
              to: "/p/$org/$project/library",
              params: address,
              label: "Results",
              icon: "query",
              end: true,
            },
          ]
        : scopes === undefined ? [] : libraryNav(address),
      badges: (scopes ?? []).map((grant) => ({
        label:
          grant.scope === "*"
            ? grantRole(grant.role)
            : `${grantRole(grant.role)} for ${grant.scope}`,
        variant: "secondary" as const,
      })),
    });
  }, [address, scopes, reviewOnly, decisionOnly, publishedView]);

  if ((isCustomerParticipantRole(role) || decisionOnly) && !libraryIndex)
    return <WorkspaceAccessDenied name="Library tools" />;

  // The pages below stay unmounted until the grant is known, but their reads are not held
  // behind it: the loaders started both on click. The Library's own page draws its frame
  // meanwhile, so its title stands from the first frame and its content lands into it.
  if (!publishedView && !retainedQueryData(me))
    return (
      <WorkspaceGate
        workspace="experience-library"
        query={me}
        pending={
          libraryIndex ? (
            <Page layout="page" role="status" aria-label="Loading the Library">
              <PageHeader title="Library" />
              <Skeleton className="h-40 w-full" />
            </Page>
          ) : undefined
        }
      />
    );

  return (
    <LibraryProjectProvider projectId={projectId}>
      <Outlet />
    </LibraryProjectProvider>
  );
}
